import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useTranslation } from 'react-i18next';
import { GripVertical, MapPin, Camera, CircleDashed } from '../ui/Icon';
import { Icon } from '../ui/Icon';
import type { Activity } from '../../types';

// Re-export Icon helper used in this file
export { Icon };

interface Props {
  activity: Activity;
  index: number;
  /** The day's colour — the badge is what tells two days apart in one list. */
  color: string;
  /** A saved place backs this stop, so selecting it opens the full card. */
  hasDetails: boolean;
  selected: boolean;
  /** False on a trip the viewer can't edit: no grip, nothing to drag. */
  draggable: boolean;
  onSelect: () => void;
}

export default function SortableStop({
  activity, index, color, hasDetails, selected, draggable, onSelect,
}: Props) {
  const { t } = useTranslation();
  // ADR-0039: the travellers' own stop, as the agency sees it — no name to show.
  const name = activity.maskedForAgency ? t('itinerary.maskedStop.title') : activity.venueName ?? activity.title;
  // isOrigin mirrors the previous day's last stop — it isn't a real independent
  // stop the user added, so it can't be dragged (same protection as List view).
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
    useSortable({ id: activity.id, disabled: activity.isOrigin || !draggable });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 10 : undefined,
  };
  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`group ${
        selected
          ? 'bg-teal-50 dark:bg-teal-900/30'
          : activity.isOrigin
            ? 'bg-slate-50 dark:bg-slate-800/40'
            : 'hover:bg-slate-50 dark:hover:bg-slate-800'
      }`}
    >
      <div className="flex items-center gap-3 px-3 py-2.5">
        {activity.isOrigin ? (
          <div className="text-teal-400 shrink-0">
            <Icon as={MapPin} size={14} />
          </div>
        ) : draggable && (
          <button
            {...attributes}
            {...listeners}
            className="text-slate-300 hover:text-slate-500 cursor-grab active:cursor-grabbing shrink-0 touch-none"
            aria-label={t('common.dragToReorder')}
          >
            <Icon as={GripVertical} size={14} />
          </button>
        )}

        {/* The row is the button: clicking anywhere on it flies the map to the
            stop, and opens its card when there's a saved place behind it. */}
        <button
          onClick={onSelect}
          className="flex items-center gap-3 flex-1 min-w-0 text-left cursor-pointer"
          aria-label={t('map.showOnMap', { name })}
        >
          <span
            className="w-7 h-7 rounded-full flex items-center justify-center text-white font-bold text-xs shrink-0"
            style={{ backgroundColor: color }}
          >
            {index + 1}
          </span>
          <span className="flex-1 min-w-0">
            <span className="flex items-center gap-1.5">
              <span className="text-sm font-semibold text-slate-800 dark:text-slate-200 truncate">
                {name}
              </span>
              {hasDetails && (
                <Icon as={Camera} size={11} className="text-slate-300 dark:text-slate-600 shrink-0" />
              )}
            </span>
            <span className="flex items-center gap-2 mt-0.5">
              {activity.isOrigin && (
                <span className="text-2xs font-semibold px-1.5 py-0.5 rounded-full bg-teal-100 dark:bg-teal-900/40 text-teal-600 dark:text-teal-400 shrink-0">
                  {t('itinerary.startFromPrev.active')}
                </span>
              )}
              {/* Same badge as the itinerary card: a stop that is optional is
                  optional in every view that shows it. */}
              {activity.isOptional && !activity.isOrigin && (
                <span
                  title={t('itinerary.optional.hint')}
                  className="inline-flex items-center gap-1 text-2xs font-semibold px-1.5 py-0.5 rounded-full border border-dashed border-slate-300 dark:border-slate-600 text-slate-500 dark:text-slate-400 shrink-0"
                >
                  <Icon as={CircleDashed} size={9} />
                  {t('itinerary.optional.badge')}
                </span>
              )}
              {activity.zone && <span className="text-xs text-slate-400 truncate">{activity.zone}</span>}
              {/* Only the stop's own fixed time. An arrival chained from travel
                  durations would be made up: it ignores how long we stay at each
                  previous stop. */}
              {activity.startTime && (
                <span className="text-xs font-semibold text-teal-600 shrink-0">{activity.startTime}</span>
              )}
            </span>
          </span>
        </button>

      </div>
    </div>
  );
}
